import { Fragment, useEffect, useState } from 'react';
import type { AuditEntry } from '../../shared/types';
import { api } from '../lib/api';
import { fmtDate } from '../lib/hooks';
import { Icon } from '../components/Icon';
import { Badge, Button, Empty, ErrorBox, Loading, Panel } from '../components/ui';

const LABELS: Record<string, string> = {
  'auth.login': 'Signed in', 'auth.logout': 'Signed out', 'auth.logout_all': 'Signed out everywhere', 'auth.link': 'Linked a sign-in method',
  'auth.unlink': 'Unlinked a sign-in method', 'auth.password.set': 'Set a password', 'auth.password.remove': 'Removed password sign-in',
  'auth.breakglass.setup': 'Break-glass account set up', 'discord.sync': 'Discord sync', 'discord.feeds': 'Channel feeds synced',
  'discord.settings': 'Discord settings changed', 'discord.channel': 'Channel feed setting changed', 'user.update': 'User updated',
  'user.sync': 'User synced', 'user.revoke_sessions': 'User signed out of all sessions', 'role.create': 'Local role created',
  'role.update': 'Local role updated', 'role.delete': 'Local role deleted', 'clearance.update': 'Clearance changed',
  'clearance.remap': 'Roles remapped', 'tool.status': 'Tool status changed', 'backup.create': 'Backup created',
  'backup.download': 'Backup downloaded', 'backup.upload': 'Backup uploaded', 'backup.restore': 'Restored from backup',
  'backup.delete': 'Backup deleted', 'backup.prune': 'Expired backups removed', 'backup.schedule': 'Backup schedule changed',
  'data.purge': 'Data purged', 'maintenance.clear': 'Maintenance lock cleared', 'discord.sync_queued': 'Discord sync queued', 'logs.export': 'System Log exported', 'revision.create': 'Revision added', 'settings.update': 'Settings changed',
};

export function describeAction(e: AuditEntry) {
  const base = LABELS[e.action] ?? e.action;
  return e.target ? `${base}: ${e.target}` : base;
}

const CATEGORIES = ['auth', 'discord', 'admin', 'backup', 'tool', 'system'];

export function LogsPage() {
  const [filters, setFilters] = useState({ q: '', category: '', outcome: '', from: '', to: '' });
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const qs = (before?: string | null) => {
    const p = new URLSearchParams();
    if (filters.q) p.set('q', filters.q);
    if (filters.category) p.set('category', filters.category);
    if (filters.outcome) p.set('outcome', filters.outcome);
    if (filters.from) p.set('from', new Date(filters.from).toISOString());
    if (filters.to) p.set('to', new Date(`${filters.to}T23:59:59`).toISOString());
    if (before) p.set('before', before);
    return p.toString();
  };

  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/api/admin/logs?${qs()}`).then((r) => { setEntries(r.entries); setCursor(r.nextCursor); setError(null); }).catch(setError);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const more = async () => {
    setLoadingMore(true);
    try { const r = await api.get(`/api/admin/logs?${qs(cursor)}`); setEntries((e) => [...(e ?? []), ...r.entries]); setCursor(r.nextCursor); }
    finally { setLoadingMore(false); }
  };

  const set = (k: keyof typeof filters) => (e: { target: { value: string } }) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="stack">
      <div className="toolbar">
        <div className="search"><Icon name="search" size={16} /><input placeholder="Search actions, people, targets" value={filters.q} onChange={set('q')} /></div>
        <select value={filters.category} onChange={set('category')} aria-label="Category"><option value="">All categories</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select value={filters.outcome} onChange={set('outcome')} aria-label="Outcome"><option value="">Any outcome</option><option value="success">Success</option><option value="failure">Failure</option></select>
        <input type="date" value={filters.from} onChange={set('from')} aria-label="From" />
        <input type="date" value={filters.to} onChange={set('to')} aria-label="To" />
        <a className="btn btn-ghost" href={`/api/admin/logs/export.csv?${qs()}`}><Icon name="download" size={16} /><span>Export CSV</span></a>
      </div>
      <ErrorBox error={error} />
      <Panel pad={false}>
        {!entries ? <Loading /> : entries.length === 0 ? <Empty icon="log" title="No log entries match" /> : (
          <div className="table-wrap">
            <table className="table log-table">
              <thead><tr><th>Time</th><th>Event</th><th>By</th><th>Category</th><th /></tr></thead>
              <tbody>
                {entries.map((e) => (
                  <Fragment key={e.id}>
                    <tr className="clickable" onClick={() => setOpen(open === e.id ? null : e.id)}>
                      <td className="nowrap muted">{fmtDate(e.at)}</td>
                      <td><span className={`dot dot-${e.outcome}`} /> {describeAction(e)}</td>
                      <td>{e.actorName}</td>
                      <td><Badge>{e.category}</Badge></td>
                      <td className="right"><Icon name={open === e.id ? 'down' : 'chevron'} size={14} /></td>
                    </tr>
                    {open === e.id && (
                      <tr className="detail-row"><td colSpan={5}><pre>{JSON.stringify({ action: e.action, target: e.target, outcome: e.outcome, ...e.details }, null, 2)}</pre></td></tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cursor && <div className="pad center"><Button busy={loadingMore} onClick={more}>Load older</Button></div>}
      </Panel>
    </div>
  );
}
